import React, { useState } from 'react';

interface PenaltyIndicatorProps {
  count: number;
  max?: number;
  correctionDates?: Date[];
}

export function PenaltyIndicator({ count, max = 4, correctionDates = [] }: PenaltyIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const percentage = Math.min((count / max) * 100, 100);
  
  let bgColor = 'bg-green-500';
  let textColor = 'text-green-400';
  
  if (count >= max) {
    bgColor = 'bg-red-500';
    textColor = 'text-red-400';
  } else if (count === max - 1) {
    bgColor = 'bg-orange-500';
    textColor = 'text-orange-400';
  } else if (count >= max / 2) {
    bgColor = 'bg-yellow-500';
    textColor = 'text-yellow-400';
  }

  // Format dates for tooltip display
  const formatDate = (date: Date) => {
    const d = new Date(date);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[d.getDay()];
    return `${dayName}, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Sort dates chronologically
  const sortedDates = [...correctionDates].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  return (
    <div 
      className="relative flex items-center gap-2"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden cursor-pointer">
        <div
          className={`h-full ${bgColor} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className={`text-sm font-medium ${textColor} min-w-[40px] text-right cursor-pointer`}>
        {count}/{max}
      </span>
      
      {/* Tooltip */}
      {showTooltip && correctionDates.length > 0 && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 min-w-[200px]">
            <div className="text-xs font-semibold text-slate-300 mb-2 border-b border-slate-600 pb-1">
              Adjustment Dates ({count})
            </div>
            <div className="space-y-1 max-h-[150px] overflow-y-auto">
              {sortedDates.map((date, index) => (
                <div key={index} className="text-xs text-slate-400">
                  {formatDate(date)}
                </div>
              ))}
            </div>
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-600" />
        </div>
      )}
      
      {/* Show message when no dates available */}
      {showTooltip && count > 0 && correctionDates.length === 0 && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3">
            <div className="text-xs text-slate-400">
              No date information available
            </div>
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-600" />
        </div>
      )}
    </div>
  );
}
